// The legal facts the web app and the services share (TF-255, TF-256), read by the web app through an
// alias (@smbsuite/legal) and by the services through the package:
//  - who else handles personal data for DoCuLat (docs/adr/0167-subprocessors.md): one list for the
//    privacy policy, the data processing agreement and the Sub-processors page, and for the tenant
//    service, which tells companies' owners when one is added;
//  - the version of the terms of service a company accepts (docs/adr/0168-terms-and-notices.md);
//  - what someone may report about a public link (the Digital Services Act's notice and action).
//
// A sub-processor added after the first list starts at least 30 days after it is listed: owners are
// told by e-mail when it is deployed, and may object before it starts (the DPA's promise). A unit test
// in the web app holds every entry to that.
//
// This file has no imports and runs in Node and in browsers alike: keep it that way.

export type SubprocessorLanguage = 'en' | 'lv';
type Words = Record<SubprocessorLanguage, string>;

export interface Subprocessor {
  /** Stable: the tenant service remembers which ids it has announced. Never reuse one. */
  id: string;
  /** The company, as its contract names it. */
  name: string;
  /** What it does for DoCuLat. */
  purpose: Words;
  /** Which personal data reaches it. */
  data: Words;
  /** Where it processes the data. */
  location: Words;
  /** Outside the European Economic Area: what makes the transfer lawful. Null inside it. */
  transfer: Words | null;
  /** 'always' for every company; 'chosen' only when a company or a person turns the feature on. */
  when: 'always' | 'chosen';
  /** The day it was put on this list (YYYY-MM-DD). */
  listed: string;
  /** The day it starts handling data: 30 days or more after `listed`, except on the first list. */
  from: string;
}

/** The day the first list was published: entries listed then were in use already. */
export const FIRST_LIST = '2026-10-13';

/** Where questions, requests and objections go. */
export const PRIVACY_EMAIL = 'privacy@doculat.eu';

/**
 * The single point of contact for authorities and users (the Digital Services Act, TF-256), for
 * questions about the terms and copyright, and for objecting to a decision on a report: a mailbox
 * somebody reads, in English and Latvian.
 */
export const CONTACT_EMAIL = 'hello@doculat.eu';

/** How long owners have between hearing of a new sub-processor and its start. */
export const NOTICE_DAYS = 30;

/**
 * The terms of service in force, as the day they were last changed (TF-256). A company stores the
 * version it accepted; when this moves, its owners are asked to accept again. Move it only for a change
 * that needs consent, together with LEGAL.updated in the web app.
 */
export const TERMS_VERSION = '2026-10-13';

/** What a report about a public link may be about (TF-256). Child abuse material may be reported anonymously. */
export const REPORT_REASONS = ['copyright', 'illegal', 'child_abuse', 'malware', 'privacy', 'other'] as const;
export type ReportReason = (typeof REPORT_REASONS)[number];
export const ANONYMOUS_REASONS: readonly ReportReason[] = ['child_abuse'];

const words = (en: string, lv: string): Words => ({ en, lv });
const EU = words('European Union', 'Eiropas Savienība');
const PRIVACY_FRAMEWORK = words(
  'The EU-US Data Privacy Framework, and the European Commission’s standard contractual clauses.',
  'ES un ASV datu privātuma regulējums un Eiropas Komisijas standartlīguma klauzulas.',
);

export const SUBPROCESSORS: readonly Subprocessor[] = [
  {
    id: 'hetzner',
    name: 'Hetzner Online GmbH',
    purpose: words('The servers DoCuLat runs on, and the storage that keeps files and backups.', 'Serveri, uz kuriem darbojas DoCuLat, un krātuve, kas glabā failus un rezerves kopijas.'),
    data: words('All data in DoCuLat. Backups are encrypted before they are stored.', 'Visi DoCuLat dati. Rezerves kopijas tiek šifrētas pirms glabāšanas.'),
    location: words('Germany and Finland (European Union)', 'Vācija un Somija (Eiropas Savienība)'),
    transfer: null,
    when: 'always',
    listed: FIRST_LIST,
    from: FIRST_LIST,
  },
  {
    id: 'cloudflare',
    name: 'Cloudflare, Inc.',
    purpose: words(
      'The network in front of DoCuLat, which protects it and passes requests on; and Turnstile, which checks that a person, not a program, sends the sign-up and company forms.',
      'Tīkls DoCuLat priekšā, kas to aizsargā un nodod pieprasījumus tālāk; un Turnstile, kas pārbauda, ka reģistrācijas un uzņēmuma formas sūta cilvēks, nevis programma.',
    ),
    data: words(
      'IP addresses and the requests passing through, encrypted until they reach it. For Turnstile, technical details of the browser and device, not what is typed into the form.',
      'IP adreses un pieprasījumi, kas iet caur to, šifrēti līdz tam. Turnstile saņem tehniskas ziņas par pārlūku un ierīci, nevis to, kas ierakstīts formā.',
    ),
    location: words('Worldwide, the data centre nearest the visitor; the company is in the United States', 'Visā pasaulē, apmeklētājam tuvākajā datu centrā; uzņēmums atrodas ASV'),
    transfer: PRIVACY_FRAMEWORK,
    when: 'always',
    listed: FIRST_LIST,
    from: FIRST_LIST,
  },
  {
    id: 'mailgun',
    name: 'Mailgun (Sinch Sweden AB)',
    purpose: words(
      'Sends invitations, shares, notifications and password e-mails, and tells DoCuLat whether each was delivered. Opening and clicks are not tracked. Receives the e-mails people send to their own DoCuLat address for filing, and keeps each for up to 3 days until DoCuLat fetches it.',
      'Sūta ielūgumus, kopīgojumus, paziņojumus un paroles e-pastus un paziņo DoCuLat, vai katrs ir piegādāts. Atvēršana un klikšķi netiek izsekoti. Saņem e-pastus, ko cilvēki sūta uz savu DoCuLat adresi saglabāšanai, un glabā katru līdz 3 dienām, līdz DoCuLat to saņem.',
    ),
    data: words(
      'The recipient’s address, and the subject and text of each e-mail; for e-mails sent in for filing, the whole e-mail with its addresses and attachments.',
      'Saņēmēja adrese, katra e-pasta temats un teksts; saglabāšanai sūtītajiem e-pastiem viss e-pasts ar adresēm un pielikumiem.',
    ),
    location: words('Its EU region, Frankfurt (European Union)', 'Tā ES reģions, Frankfurte (Eiropas Savienība)'),
    transfer: null,
    when: 'always',
    listed: FIRST_LIST,
    from: FIRST_LIST,
  },
  {
    id: 'uptimerobot',
    name: 'UptimeRobot (Ultimate Robot Company)',
    purpose: words(
      'The public status page, status.doculat.eu, which says whether DoCuLat is up. It checks the service from outside.',
      'Publiskā statusa lapa status.doculat.eu, kas rāda, vai DoCuLat darbojas. Tas pārbauda pakalpojumu no ārpuses.',
    ),
    data: words(
      'No customer data. A person who subscribes to its updates gives it their e-mail address, which DoCuLat does not receive.',
      'Nekādi klientu dati. Kas pierakstās tā jaunumiem, tam dod savu e-pasta adresi, un DoCuLat to nesaņem.',
    ),
    location: EU,
    transfer: null,
    when: 'always',
    listed: FIRST_LIST,
    from: FIRST_LIST,
  },
  {
    id: 'atlassian',
    name: 'Atlassian (Jira)',
    purpose: words(
      '“Report a problem” in the app: the report goes to DoCuLat’s issue tracker.',
      '“Ziņot par problēmu” lietotnē: ziņojums nonāk DoCuLat problēmu reģistrā.',
    ),
    data: words(
      'Only when a person sends a report: their name, e-mail address, what they write, and the page and browser it came from.',
      'Tikai tad, ja cilvēks nosūta ziņojumu: viņa vārds, e-pasta adrese, tas, ko viņš raksta, un lapa un pārlūks, no kura tas nāk.',
    ),
    location: words('Atlassian’s cloud; the company is in Australia and the United States', 'Atlassian mākonis; uzņēmums atrodas Austrālijā un ASV'),
    transfer: PRIVACY_FRAMEWORK,
    when: 'chosen',
    listed: FIRST_LIST,
    from: FIRST_LIST,
  },
  {
    id: 'stripe',
    name: 'Stripe Payments Europe, Ltd.',
    purpose: words('Card payments, when a company pays by card. Stripe keeps the card; DoCuLat keeps the amounts.', 'Karšu maksājumi, ja uzņēmums maksā ar karti. Karti glabā Stripe, summas glabā DoCuLat.'),
    data: words('The payer’s name, e-mail address and card. The card number never reaches DoCuLat.', 'Maksātāja vārds, e-pasta adrese un karte. Kartes numurs DoCuLat nekad nenonāk.'),
    location: words('Ireland (European Union) and the United States', 'Īrija (Eiropas Savienība) un ASV'),
    transfer: PRIVACY_FRAMEWORK,
    when: 'chosen',
    listed: FIRST_LIST,
    from: FIRST_LIST,
  },
  {
    id: 'microsoft',
    name: 'Microsoft Ireland Operations Ltd.',
    purpose: words('OneDrive and SharePoint, when a company connects its own drive.', 'OneDrive un SharePoint, ja uzņēmums pievieno savu disku.'),
    data: words('The files the company chooses to open or save there.', 'Faili, ko uzņēmums izvēlas tur atvērt vai saglabāt.'),
    location: words('As the company’s own Microsoft contract says', 'Kā noteikts uzņēmuma paša līgumā ar Microsoft'),
    transfer: null,
    when: 'chosen',
    listed: FIRST_LIST,
    from: FIRST_LIST,
  },
  {
    id: 'google',
    name: 'Google Ireland Limited',
    purpose: words(
      'Google Drive, when a company connects it; Google Contacts, when a person imports their own address book.',
      'Google Drive, ja uzņēmums to pievieno; Google Contacts, ja cilvēks importē savu adrešu grāmatu.',
    ),
    data: words('The files the company chooses; the contacts the person imports.', 'Faili, ko uzņēmums izvēlas; kontakti, ko cilvēks importē.'),
    location: words('As the company’s or the person’s own Google account says', 'Kā noteikts uzņēmuma vai cilvēka paša Google kontā'),
    transfer: null,
    when: 'chosen',
    listed: FIRST_LIST,
    from: FIRST_LIST,
  },
  {
    id: 'dokobit',
    name: 'Dokobit, UAB',
    purpose: words('Electronic signatures, when a company sends a document for signing.', 'Elektroniskie paraksti, ja uzņēmums nosūta dokumentu parakstīšanai.'),
    data: words('The document, and the signers’ names, e-mail addresses and signatures.', 'Dokuments un parakstītāju vārdi, e-pasta adreses un paraksti.'),
    location: words('Lithuania (European Union)', 'Lietuva (Eiropas Savienība)'),
    transfer: null,
    when: 'chosen',
    listed: FIRST_LIST,
    from: FIRST_LIST,
  },
  {
    // Push to the phone app (TF-272, docs/adr/0191-push-notifications.md): the notifications service
    // pushes nothing before `from`, so owners can object first.
    id: 'expo',
    name: '650 Industries, Inc. (Expo)',
    purpose: words(
      'Push notifications to the DoCuLat app on a phone, when a person allows them. Expo passes each one to Apple’s or Google’s push service, which shows it on the phone.',
      'Paziņojumi DoCuLat lietotnei tālrunī, ja cilvēks tos atļauj. Expo katru nodod Apple vai Google paziņojumu pakalpojumam, kas to parāda tālrunī.',
    ),
    data: words(
      'The phone’s push address, what happened and the name of the record it is about (a task’s subject, a file’s name), and the link to open. Never a note, an amount or a file.',
      'Tālruņa paziņojumu adrese, kas notika un ieraksta nosaukums (uzdevuma temats, faila nosaukums), un saite, ko atvērt. Nekad piezīme, summa vai fails.',
    ),
    location: words('The United States', 'ASV'),
    transfer: words('The European Commission’s standard contractual clauses.', 'Eiropas Komisijas standartlīguma klauzulas.'),
    when: 'chosen',
    listed: '2026-10-18',
    from: '2026-11-17',
  },
  {
    // TF-364: address search in a meeting's place field, for companies whose maps are Google's. The
    // CRM asks nothing of Google before `from`, so owners can object first.
    id: 'google-places',
    name: 'Google Ireland Limited (Google Maps Platform)',
    purpose: words(
      'Address search when a meeting’s place is typed, for companies whose maps are Google’s (Settings, Company).',
      'Adrešu meklēšana, rakstot sapulces vietu, uzņēmumiem, kuru kartes ir Google (Iestatījumi, Uzņēmums).',
    ),
    data: words(
      'What a person types in a meeting’s place field, and the place they pick. Never their name, a record or the meeting.',
      'Tas, ko cilvēks raksta sapulces vietas laukā, un vieta, ko viņš izvēlas. Nekad viņa vārds, ieraksts vai pati sapulce.',
    ),
    location: words('Google’s servers, including in the United States', 'Google serveri, arī ASV'),
    transfer: PRIVACY_FRAMEWORK,
    when: 'chosen',
    listed: '2026-10-19',
    from: '2026-11-18',
  },
];

/** The day push to the phone app may start (TF-272): Expo's entry above, 30 days after it was listed. */
export const PUSH_FROM = SUBPROCESSORS.find((entry) => entry.id === 'expo')!.from;

/** The day address search through Google may start (TF-364): its entry above, 30 days after it was listed. */
export const GOOGLE_PLACES_FROM = SUBPROCESSORS.find((entry) => entry.id === 'google-places')!.from;

/** Days between two YYYY-MM-DD dates. */
export function daysBetween(from: string, to: string): number {
  return Math.round((Date.parse(`${to}T00:00:00Z`) - Date.parse(`${from}T00:00:00Z`)) / 86_400_000);
}

/** The entries that break the notice rule: listed after the first list but starting too soon. */
export function shortNotice(list: readonly Subprocessor[] = SUBPROCESSORS): Subprocessor[] {
  return list.filter((entry) => entry.listed !== FIRST_LIST && daysBetween(entry.listed, entry.from) < NOTICE_DAYS);
}
